import { SyntheticEvent, useEffect } from 'react';
import { createProvider } from '@frontend/store';
import { useModalControl } from './use-modal-control';

type ModalSingletonStore = {
  modals: {
    id: string;
    isOpen: boolean;
    onClose: () => void;
  }[];
  closeModal: () => void;
  getOpenModal: () => { id: string; isOpen: boolean; onClose: () => void } | undefined;
  openModal: (key: string) => void;
  registerModal: (key: string, onClose: () => void) => void;
  unregisterModal: (key: string) => void;
};
export const { Provider: ModalSingletonProvider, useStore: useModalSingletonStore } =
  createProvider<ModalSingletonStore>()((set, get) => ({
    modals: [] as ModalSingletonStore['modals'],
    closeModal: () => {
      set((state) => {
        const modals = state.modals.map((modal) => {
          modal.isOpen = false;
          modal.onClose();
          return modal;
        });

        return { ...state, modals };
      });
    },
    getOpenModal: () => {
      return get().modals.find((modal) => modal.isOpen);
    },
    openModal: (key) => {
      set((state) => {
        const modals = state.modals.map((modal) => {
          if (modal.id === key) {
            modal.isOpen = true;
          } else {
            modal.isOpen = false;
            modal.onClose();
          }
          return modal;
        });

        return { ...state, modals };
      });
    },
    registerModal: (key, onClose) => {
      set((state) => {
        const modals = state.modals.filter((modal) => modal.id !== key);
        modals.push({ id: key, isOpen: false, onClose });

        return { ...state, modals };
      });
    },
    unregisterModal: (key) => {
      set((state) => {
        const modals = state.modals.filter((modal) => modal.id !== key);

        return { ...state, modals };
      });
    },
  }));

export const useModalSingletonControl = (id: string) => {
  const { triggerProps, modalProps } = useModalControl();

  const modalSingletonStore = useModalSingletonStore(['registerModal', 'unregisterModal', 'openModal', 'closeModal']);

  useEffect(() => {
    modalSingletonStore.registerModal(id, modalProps.onClose);
    return () => {
      modalSingletonStore.unregisterModal(id);
    };
  }, []);

  const onClickWrapped = (e?: SyntheticEvent) => {
    modalSingletonStore.openModal(id);
    triggerProps.onClick(e);
  };

  return {
    modalProps: { ...modalProps, onClose: modalSingletonStore.closeModal },
    triggerProps: { ...triggerProps, onClick: onClickWrapped },
  };
};
